// src/services/CompetitiveResearchWebSocketService.js
// Dedicated WebSocket service for Competitive Research
// Full-featured version with heartbeat, reconnect, error handling

import { getCookie } from '../utils/auth';

const getCompetitiveResearchWsUrl = (userId = null) => {
  const wsUrl = process.env.REACT_APP_COMPETITIVE_RESEARCH_WS_URL;
  let baseUrl;
  
  if (!wsUrl) {
    console.warn("REACT_APP_COMPETITIVE_RESEARCH_WS_URL not set, using fallback");
    baseUrl = "wss://56ewfl1za5.execute-api.us-east-1.amazonaws.com/prod";
  } else {
    baseUrl = wsUrl;
  }
  
  // Get JWT access token for authentication
  const accessToken = getCookie('accessToken');
  if (!accessToken) {
    throw new Error('No access token found for WebSocket authentication');
  }
  
  let url = `${baseUrl}?token=${encodeURIComponent(accessToken)}`;
  if (userId) {
    url += `&userId=${encodeURIComponent(userId)}`;
  }
  return url;
};

class CompetitiveResearchWebSocketService {
  constructor() {
    this.ws = null;
    this.connectionId = null;
    this.userId = null;
    this.isConnecting = false;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.messageHandlers = new Map();
    this.connectionHandlers = new Set();
    this.errorHandlers = new Set();
    this.messageQueue = [];
    this.heartbeatInterval = null;
    this.missedPongs = 0;
    
    // Listen for token refresh events from global auth
    this.setupTokenRefreshListener();
  }

  async connect(userId = null) {
    if (this.isConnected || this.isConnecting) {
      console.log('🔄 [CompetitiveResearch] WebSocket already connected or connecting');
      return Promise.resolve();
    }

    if (userId) {
      this.userId = userId;
    }

    return new Promise((resolve, reject) => {
      try {
        this.isConnecting = true;
        const wsUrl = getCompetitiveResearchWsUrl(this.userId);
        console.log(`📡 [CompetitiveResearch] Connecting to WebSocket: ${wsUrl}`);
        
        this.ws = new WebSocket(wsUrl);
        
        this.ws.onopen = () => {
          console.log('✅ [CompetitiveResearch] WebSocket connected successfully');
          this.isConnected = true;
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          
          this.startHeartbeat();
          this.processMessageQueue();
          
          this.connectionHandlers.forEach(handler => {
            try {
              handler({ type: 'connected', connectionId: this.connectionId });
            } catch (error) {
              console.error('[CompetitiveResearch] Error in connection handler:', error);
            }
          });
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log(`📥 [CompetitiveResearch] Message type: ${message.type || message.action}`);
            
            if (message.type === 'connected') {
              this.connectionId = message.data?.connectionId;
              console.log(`🔗 [CompetitiveResearch] Connection ID: ${this.connectionId}`);
            }
            
            if (message.type === 'pong' || message.action === 'pong') {
              this.missedPongs = 0;
              console.log('💓 [CompetitiveResearch] Pong received - connection healthy');
              return;
            }
            
            const handlers = this.messageHandlers.get(message.type) || [];
            handlers.forEach(handler => {
              try {
                handler(message);
              } catch (error) {
                console.error(`[CompetitiveResearch] Error in message handler for ${message.type}:`, error);
              }
            });
            
            const genericHandlers = this.messageHandlers.get('*') || [];
            genericHandlers.forEach(handler => {
              try {
                handler(message);
              } catch (error) {
                console.error('[CompetitiveResearch] Error in generic message handler:', error);
              }
            });
            
          } catch (error) {
            console.error('[CompetitiveResearch] Error parsing WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('❌ [CompetitiveResearch] WebSocket error:', error);
          this.errorHandlers.forEach(handler => {
            try {
              handler(error);
            } catch (handlerError) {
              console.error('[CompetitiveResearch] Error in error handler:', handlerError);
            }
          });
        };

        this.ws.onclose = (event) => {
          console.log(`🔌 [CompetitiveResearch] WebSocket closed: ${event.code} - ${event.reason}`);
          this.isConnected = false;
          this.isConnecting = false;
          this.connectionId = null;
          
          this.stopHeartbeat();
          
          this.connectionHandlers.forEach(handler => {
            try {
              handler({ type: 'disconnected', code: event.code, reason: event.reason });
            } catch (error) {
              console.error('[CompetitiveResearch] Error in disconnection handler:', error);
            }
          });
          
          // Handle authentication errors (1006 often indicates auth failure)
          if (event.code === 1006 || event.code === 1011) {
            console.log('🔐 [CompetitiveResearch] Possible authentication error, will refresh token on reconnect');
          }
          
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          } else if (this.isConnecting) {
            reject(new Error(`WebSocket connection failed: ${event.code} - ${event.reason}`));
          }
        };

        setTimeout(() => {
          if (this.isConnecting) {
            console.log('⏰ [CompetitiveResearch] WebSocket connection timeout');
            this.ws.close();
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000);

      } catch (error) {
        console.error('❌ [CompetitiveResearch] Error creating WebSocket connection:', error);
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  scheduleReconnect() {
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    console.log(`🔄 [CompetitiveResearch] Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      if (!this.isConnected && this.reconnectAttempts <= this.maxReconnectAttempts) {
        console.log(`🔄 [CompetitiveResearch] Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        this.connect(this.userId).catch(error => {
          console.error('[CompetitiveResearch] Reconnection failed:', error);
        });
      }
    }, delay);
  }

  disconnect() {
    if (this.ws) {
      console.log('🔌 [CompetitiveResearch] Disconnecting WebSocket');
      this.stopHeartbeat();
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
    this.connectionId = null;
    this.reconnectAttempts = 0;
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.missedPongs = 0;
    console.log('💓 [CompetitiveResearch] Starting heartbeat (5 minute interval)');
    
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.ws) {
        try {
          if (this.missedPongs >= 3) {
            console.error('❌ [CompetitiveResearch] Connection appears dead (3 missed pongs), reconnecting...');
            this.ws.close(4000, 'Heartbeat timeout');
            return;
          }
          
          this.ws.send(JSON.stringify({ action: 'ping' }));
          this.missedPongs++;
          console.log('💓 [CompetitiveResearch] Heartbeat ping sent');
        } catch (error) {
          console.error('❌ [CompetitiveResearch] Error sending heartbeat:', error);
        }
      }
    }, 5 * 60 * 1000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      this.missedPongs = 0;
      console.log('💓 [CompetitiveResearch] Heartbeat stopped');
    }
  }

  sendMessage(message) {
    if (!this.isConnected) {
      console.log('📋 [CompetitiveResearch] Queueing message (not connected):', message.action);
      this.messageQueue.push(message);
      
      if (!this.isConnecting) {
        this.connect().catch(error => {
          console.error('[CompetitiveResearch] Failed to connect for message sending:', error);
        });
      }
      return;
    }

    try {
      const messageStr = JSON.stringify(message);
      console.log(`📤 [CompetitiveResearch] Sending WebSocket message: ${message.action}`);
      this.ws.send(messageStr);
    } catch (error) {
      console.error('❌ [CompetitiveResearch] Error sending WebSocket message:', error);
      throw error;
    }
  }

  processMessageQueue() {
    if (this.messageQueue.length > 0) {
      console.log(`📋 [CompetitiveResearch] Processing ${this.messageQueue.length} queued messages`);
      const messages = [...this.messageQueue];
      this.messageQueue = [];
      
      messages.forEach(message => {
        try {
          this.sendMessage(message);
        } catch (error) {
          console.error('[CompetitiveResearch] Error sending queued message:', error);
        }
      });
    }
  }

  onMessage(messageType, handler) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, []);
    }
    this.messageHandlers.get(messageType).push(handler);
    
    return () => {
      const handlers = this.messageHandlers.get(messageType);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  onConnection(handler) {
    this.connectionHandlers.add(handler);
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  onError(handler) {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  clearHandlers() {
    this.messageHandlers.clear();
    this.connectionHandlers.clear();
    this.errorHandlers.clear();
  }

  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      connectionId: this.connectionId,
      reconnectAttempts: this.reconnectAttempts,
      queuedMessages: this.messageQueue.length
    };
  }

  setupTokenRefreshListener() {
    // Custom event listener for token refresh
    window.addEventListener('tokenRefreshed', () => {
      if (this.isConnected) {
        console.log('🔄 [CompetitiveResearch] Token refreshed globally, reconnecting WebSocket');
        this.ws.close(1000, 'Token refresh');
      }
    });
  }
}

const competitiveResearchWebSocketService = new CompetitiveResearchWebSocketService();
export default competitiveResearchWebSocketService;
